/**
 * Helpers de presentación de la categoría *Configuración*.
 */

import type { NivelLog } from '../configuracion.types';

const UNIDADES = ['B', 'KB', 'MB', 'GB'];

/** Tamaño legible ("935 KB", "1.2 MB"). */
export function formatearBytes(bytes: number | null | undefined): string {
  let valor = Number(bytes ?? 0);
  let indice = 0;

  while (valor >= 1024 && indice < UNIDADES.length - 1) {
    valor /= 1024;
    indice += 1;
  }

  const decimales = indice === 0 ? 0 : valor < 10 ? 1 : 0;

  return `${valor.toFixed(decimales)} ${UNIDADES[indice]}`;
}

/** Etiqueta del nivel de una línea de log. */
export const ETIQUETA_NIVEL: Record<NivelLog, string> = {
  trace: 'Traza',
  debug: 'Debug',
  info: 'Info',
  warn: 'Aviso',
  error: 'Error',
  fatal: 'Crítico',
};

/**
 * Color de cada nivel dentro de la consola (fondo oscuro).
 *
 * El gris queda para `info`: así lo importante salta a la vista y una consola
 * mayormente informativa no se ve como una pared de colores. Los extremos se
 * separan con la opacidad: `trace` es el más apagado (es el más ruido) y
 * `fatal` el único en negrita.
 */
export const COLOR_NIVEL: Record<NivelLog, string> = {
  trace: 'text-ink-500',
  debug: 'text-ink-400',
  info: 'text-ink-200',
  warn: 'text-amber-300',
  error: 'text-rose-400',
  fatal: 'text-rose-200 font-bold',
};

/** Color del punto del nivel, para las pestañas de filtro. */
export const PUNTO_NIVEL: Record<NivelLog, string> = {
  trace: 'bg-ink-200',
  debug: 'bg-ink-300',
  info: 'bg-ink-400',
  warn: 'bg-amber-400',
  error: 'bg-rose-500',
  fatal: 'bg-rose-700',
};

/**
 * Tono del `Badge` de cada nivel, para el detalle de una línea.
 *
 * En el detalle (fondo claro) `info` va en el color de marca para que un registro
 * normal no se vea como una alerta; `trace`/`debug` quedan neutros.
 */
export const TONO_NIVEL: Record<NivelLog, 'neutral' | 'brand' | 'warning' | 'danger'> = {
  trace: 'neutral',
  debug: 'neutral',
  info: 'brand',
  warn: 'warning',
  error: 'danger',
  fatal: 'danger',
};

/** Fecha `Y-m-d` de hoy y primer día del mes en curso. */
export function rangoMesActual(): { fecha_inicio: string; fecha_fin: string } {
  const hoy = new Date();
  const dosDigitos = (valor: number) => String(valor).padStart(2, '0');
  const mes = `${hoy.getFullYear()}-${dosDigitos(hoy.getMonth() + 1)}`;

  return { fecha_inicio: `${mes}-01`, fecha_fin: `${mes}-${dosDigitos(hoy.getDate())}` };
}

/** `01/09/2026` a partir de `2026-09-01` (sin pasar por `Date`, que desfasa). */
export function fechaCorta(valor: string | null | undefined): string {
  if (!valor) return '—';

  const partes = valor.substring(0, 10).split('-');

  return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : valor;
}

/** Cantidad de líneas legible ("7,643"). */
export function formatearLineas(lineas: number | null | undefined): string {
  if (lineas === null || lineas === undefined) return '—';

  return lineas.toLocaleString('es-PE');
}

/** Mes en curso como valor de `<input type="month">` (`YYYY-MM`). */
export function mesActual(): string {
  const hoy = new Date();

  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
}

/** Convierte un mes (`YYYY-MM`) al rango de fechas que espera la API. */
export function periodoDeMes(mes: string): { fecha_inicio: string; fecha_fin: string } {
  const [anio, numeroMes] = mes.split('-').map((parte) => Number(parte));
  const ultimoDia = new Date(anio ?? 0, numeroMes ?? 0, 0).getDate();
  const dosDigitos = (valor: number) => String(valor).padStart(2, '0');

  return {
    fecha_inicio: `${anio}-${dosDigitos(numeroMes ?? 0)}-01`,
    fecha_fin: `${anio}-${dosDigitos(numeroMes ?? 0)}-${dosDigitos(ultimoDia)}`,
  };
}
